import { useEffect, useState } from 'react';
import { MessageCircle, PanelRightClose } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { CentralChatPanelBody } from './CentralChatPanelBody';
import { useCentralChatRuntime } from './centralChatRuntime';
import { useRailResize } from './useRailResize';

const STRIP_WIDTH = 48;
// Matches the layout's mobile breakpoint (Tailwind `md`, sidebar's useIsMobile).
const NARROW_BREAKPOINT = 768;

function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const check = () => setNarrow(window.innerWidth < NARROW_BREAKPOINT);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return narrow;
}

function tenantDomain(tenantId: string | null): string {
  if (!tenantId) return 'no workspace';
  return tenantId.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

/**
 * The MJ rail — Emjae docked on the right of the app shell.
 *
 * Desktop (>= 768px): an in-flow flex sibling of the page content, so expanding
 * it NARROWS the page rather than covering it. Collapsed, it is a ~48px strip;
 * the sticky app header (with the workspace switcher) is never covered because
 * the rail sits beside the content. The left edge is a drag/keyboard separator.
 *
 * Narrow screens: a fixed collapsed tab plus an overlay Sheet from the right.
 * Only one branch mounts, so there is never a duplicate live-region thread.
 *
 * Expanded/collapsed state is the runtime's `bubbleOpen` (persisted per
 * tenant+user), so the audit drawer's "Chat with AI" expands the rail for free.
 * Width is persisted in localStorage via useRailResize.
 */
export function CentralChatRail() {
  const chat = useCentralChatRuntime();
  const resize = useRailResize();
  const isNarrow = useIsNarrow();
  const expanded = chat.bubbleOpen;

  const pageLabel = chat.activePage?.label ?? chat.contextLabel;
  const domain = tenantDomain(chat.tenantId);

  const subtitle = (
    <span className="inline-flex max-w-full items-center gap-1.5">
      <span className="truncate">{domain}</span>
      <span className="shrink-0 rounded border border-warm-border bg-paper-2 px-1 py-px normal-case tracking-normal text-ink-2">
        {pageLabel}
      </span>
    </span>
  );

  if (isNarrow) {
    return (
      <>
        {!expanded && (
          <button
            type="button"
            onClick={chat.openBubble}
            title="Open Emjae"
            aria-label="Open Emjae"
            className="fixed right-0 top-1/2 z-40 flex -translate-y-1/2 flex-col items-center gap-2 rounded-l-lg border border-r-0 border-warm-border bg-ink px-2 py-3 text-white shadow-lg"
          >
            <MessageCircle className="size-5" />
            <span className="font-mono text-[10px] uppercase tracking-widest [writing-mode:vertical-rl]">
              Emjae
            </span>
            {chat.unreadCount > 0 && (
              <span
                className="absolute -left-1 top-1.5 size-2 rounded-full bg-primary ring-2 ring-ink"
                aria-hidden="true"
              />
            )}
          </button>
        )}
        <Sheet
          open={expanded}
          onOpenChange={(open) => (open ? chat.openBubble() : chat.closeBubble())}
        >
          <SheetContent
            side="right"
            className="flex w-[min(26rem,calc(100vw-2rem))] flex-col gap-0 p-0 sm:max-w-none"
          >
            <SheetTitle className="sr-only">Emjae chat</SheetTitle>
            <CentralChatPanelBody subtitle={subtitle} showStatusBanner onLeave={chat.closeBubble} />
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <div
      className="shrink-0"
      style={{ width: expanded ? resize.width : STRIP_WIDTH }}
      data-mj-rail={expanded ? 'expanded' : 'collapsed'}
    >
      <div className="sticky top-0 h-svh">
        {expanded ? (
          <section
            aria-label="Emjae chat"
            className="relative flex h-full min-h-0 flex-col border-l border-warm-border bg-warm-bg text-ink"
          >
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize chat panel"
              aria-valuenow={resize.width}
              aria-valuemin={resize.min}
              aria-valuemax={resize.max}
              tabIndex={0}
              onMouseDown={(e) => {
                e.preventDefault();
                resize.startDrag();
              }}
              onKeyDown={resize.onSeparatorKeyDown}
              className="group/resize absolute left-0 top-0 bottom-0 z-10 flex w-1.5 cursor-col-resize items-center focus-visible:outline-none"
            >
              <div className="absolute inset-y-0 left-0 w-1.5 bg-transparent transition-colors group-hover/resize:bg-coral/20 group-focus-visible/resize:bg-ring/40" />
            </div>
            <CentralChatPanelBody
              subtitle={subtitle}
              chromeActions={
                <button
                  type="button"
                  onClick={chat.closeBubble}
                  title="Collapse chat"
                  aria-label="Collapse chat"
                  className="grid size-8 place-items-center rounded-md text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
                >
                  <PanelRightClose className="size-4" />
                </button>
              }
              showStatusBanner
            />
          </section>
        ) : (
          <button
            type="button"
            onClick={chat.openBubble}
            title="Open Emjae"
            aria-label="Open Emjae"
            className="group relative flex h-full w-full flex-col items-center gap-3 border-l border-warm-border bg-paper py-3 text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
          >
            <MessageCircle className="size-5 shrink-0" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3 [writing-mode:vertical-rl]">
              Emjae
            </span>
            {chat.unreadCount > 0 && (
              <span
                className="absolute right-1.5 top-1.5 size-2 rounded-full bg-primary ring-2 ring-paper"
                aria-hidden="true"
              />
            )}
          </button>
        )}
      </div>
    </div>
  );
}
